import { useAuth } from './useAuth'
import { useEventQuotaState } from './useEvents'
import { useMyOrganization } from './useMyOrganization'

export type SubscriptionTier = 'starter' | 'professional' | 'enterprise'

const TIER_RANK: Record<SubscriptionTier, number> = {
  starter: 0,
  professional: 1,
  enterprise: 2,
}

function normalizeTier(value: unknown): SubscriptionTier {
  if (value === 'pro' || value === 'professional') return 'professional'
  return value === 'enterprise' ? 'enterprise' : 'starter'
}

/**
 * Effective tier = max(personal tier, active-org tier).
 *
 * Membership in an org with `pro` or `enterprise` tier upgrades the user
 * for capability gates — they get the org's features even if their
 * personal profile is still starter. This is how invoice-billed
 * enterprise orgs unlock branding for every member without needing to
 * pay-per-seat on Stripe.
 */
export function useSubscription() {
  const { profile, isLoading: authLoading } = useAuth()
  // This is the exact owner-wide population used by trg_enforce_event_quota.
  // Workspace visibility is intentionally narrower and cannot decide paid
  // gates or the first-event exemption.
  const {
    hasCreatedEvent,
    activeEventCount,
    loading: eventsLoading,
  } = useEventQuotaState()
  const { active: activeOrg, loading: orgLoading } = useMyOrganization()

  // Tier-only gates (for example RequireProAccess) do not depend on the event
  // quota RPC. Keeping these states separate prevents an unrelated count read
  // from delaying a route whose answer is already known from auth + org.
  const loading = authLoading || orgLoading
  // First-event and quota-dependent decisions need both the effective tier and
  // the authoritative owner-wide count. Dashboard, Events and Offer Builder
  // use this stronger boundary before opening or closing paid capabilities.
  const entitlementsLoading = loading || eventsLoading

  const personalTier = normalizeTier(profile?.subscription_tier)
  const personalStatus = profile?.subscription_status ?? 'active'
  const personalActive = personalStatus === 'active' || personalStatus === 'trialing'

  const orgTierRaw = normalizeTier(activeOrg?.org.subscription_tier)
  const orgActive =
    activeOrg !== null &&
    (activeOrg.org.subscription_status === 'active' ||
      activeOrg.org.subscription_status === 'trialing')

  // Effective tier is the better of personal and org (when org is active)
  const effectivePersonal = personalActive ? personalTier : 'starter'
  const effectiveOrg = orgActive ? orgTierRaw : 'starter'
  const tier: SubscriptionTier =
    TIER_RANK[effectivePersonal] >= TIER_RANK[effectiveOrg]
      ? effectivePersonal
      : effectiveOrg

  const isActive = personalActive || orgActive
  const isPro = isActive && (tier === 'professional' || tier === 'enterprise')
  const isEnterprise = isActive && tier === 'enterprise'

  // WHOSE plan is in force. The comparison above already knows; until now it
  // threw the answer away, so `/settings/billing` showed four of Nortura
  // PROFF's five admins the word «Enterprise» next to the payment state of
  // their own unpaid profile («Ingen aktiv betaling») and never named the
  // organisation the plan actually comes from.
  //
  // Strictly greater, not `>=`: when the personal plan already reaches the
  // same tier it is the one in force, and attributing it to the organisation
  // would tell the owner their own paid plan belongs to someone else.
  const planOrg =
    TIER_RANK[effectiveOrg] > TIER_RANK[effectivePersonal] && activeOrg
      ? { id: activeOrg.org.id, name: activeOrg.org.name }
      : null
  // The payment state of whatever grants the plan. Reading the personal
  // column when the organisation is paying describes the wrong agreement.
  const planStatus = planOrg
    ? activeOrg?.org.subscription_status ?? 'active'
    : personalStatus

  const maxEvents = !hasCreatedEvent ? Infinity : isPro ? Infinity : 1
  const canUseOfferBuilder = !hasCreatedEvent ? true : isPro
  const canUseTeamRoles = !hasCreatedEvent ? true : isPro
  const canUseBranding = !hasCreatedEvent ? true : isEnterprise
  // SMS costs the operator real money per message, so unlike the gates above
  // it carries NO first-event exemption. The server enforces the same rule on
  // the EVENT OWNER's tier (supabase/functions/_shared/entitlements.ts:
  // starter = 0, professional = 100/mnd, enterprise unlimited); this flag only
  // lets the UI gate itself before the 403 does.
  const canUseSms = isPro

  return {
    tier,
    status: personalStatus,
    /** The organisation whose plan is in force, or null when it is the user's own. */
    planOrg,
    /** Payment state of whatever grants the plan: the org's when `planOrg` is set. */
    planStatus,
    loading,
    entitlementsLoading,
    isActive,
    isPro,
    isEnterprise,
    hasCreatedEvent,
    activeEventCount,
    /** False only once the count is real. «Et tall vi ikke har talt, vises ikke.» */
    eventsLoading,
    maxEvents,
    canUseOfferBuilder,
    canUseTeamRoles,
    canUseBranding,
    canUseSms,
  }
}
